import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Alert,
  Tabs,
  Descriptions,
  Tag,
  Divider,
  Typography,
  Row,
  Col,
  List,
} from 'antd';
import {
  SendOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import api, { get } from '../../utils/api';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;

const EmailTest = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);
  
  const [verificationForm] = Form.useForm();
  const [welcomeForm] = Form.useForm();
  const [passwordResetForm] = Form.useForm();
  const [customForm] = Form.useForm();

  useEffect(() => {
    fetchStatus();
  }, []);

  // 获取服务状态
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await get('/admin/email/status');
      if (res.code === 200) {
        setStatus(res.data);
      }
    } catch (error) {
      message.error('获取服务状态失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 添加测试结果
  const addTestResult = (type, success, message, data = {}) => {
    const result = {
      id: Date.now(),
      type,
      success,
      message,
      data,
      timestamp: new Date().toLocaleString(),
    };
    setTestResults(prev => [result, ...prev].slice(0, 10)); // 只保留最近10条
  };

  // 发送验证码邮件
  const handleSendVerificationCode = async (values) => {
    try {
      const res = await api.post('/admin/email/test/verification-code', {
        to: values.email,
      });
      
      if (res.code === 200) {
        message.success('验证码邮件发送成功');
        addTestResult('验证码', true, '发送成功', {
          email: values.email,
          code: res.data.testCode,
          messageId: res.data.messageId,
        });
        verificationForm.resetFields();
      } else {
        message.error(res.message || '发送失败');
        addTestResult('验证码', false, res.message || '发送失败');
      }
    } catch (error) {
      message.error(error.message || '发送失败');
      addTestResult('验证码', false, error.message || '发送失败');
    }
  };

  // 发送欢迎邮件
  const handleSendWelcome = async (values) => {
    try {
      const res = await api.post('/admin/email/test/welcome', {
        to: values.email,
        username: values.username,
      });
      
      if (res.code === 200) {
        message.success('欢迎邮件发送成功');
        addTestResult('欢迎邮件', true, '发送成功', {
          email: values.email,
          username: values.username,
          messageId: res.data.messageId,
        });
        welcomeForm.resetFields();
      } else {
        message.error(res.message || '发送失败');
        addTestResult('欢迎邮件', false, res.message || '发送失败');
      }
    } catch (error) {
      message.error(error.message || '发送失败');
      addTestResult('欢迎邮件', false, error.message || '发送失败');
    }
  };

  // 发送密码重置邮件
  const handleSendPasswordReset = async (values) => {
    try {
      const res = await api.post('/admin/email/test/password-reset', {
        to: values.email,
      });
      
      if (res.code === 200) {
        message.success('密码重置邮件发送成功');
        addTestResult('密码重置', true, '发送成功', {
          email: values.email,
          messageId: res.data.messageId,
        });
        passwordResetForm.resetFields();
      } else {
        message.error(res.message || '发送失败');
        addTestResult('密码重置', false, res.message || '发送失败');
      }
    } catch (error) {
      message.error(error.message || '发送失败');
      addTestResult('密码重置', false, error.message || '发送失败');
    }
  };

  // 发送自定义邮件
  const handleSendCustom = async (values) => {
    try {
      const res = await api.post('/admin/email/test/send', {
        to: values.email,
        subject: values.subject,
        content: values.content,
      });
      
      if (res.code === 200) {
        message.success('自定义邮件发送成功');
        addTestResult('自定义邮件', true, '发送成功', {
          email: values.email,
          subject: values.subject,
          messageId: res.data.messageId,
        });
        customForm.resetFields();
      } else {
        message.error(res.message || '发送失败');
        addTestResult('自定义邮件', false, res.message || '发送失败');
      }
    } catch (error) {
      message.error(error.message || '发送失败');
      addTestResult('自定义邮件', false, error.message || '发送失败');
    }
  };

  return (
    <div>
      <Row gutter={16}>
        <Col span={16}>
          <Card
            title={
              <Space>
                <MailOutlined />
                <span>邮件发送测试</span>
              </Space>
            }
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchStatus}
                loading={loading}
              >
                刷新状态
              </Button>
            }
          >
            {/* 服务状态 */}
            {status && (
              <Alert
                message="服务状态"
                description={
                  <Descriptions column={3} size="small">
                    <Descriptions.Item label="服务">
                      {status.enabled ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>已启用</Tag>
                      ) : (
                        <Tag color="default" icon={<CloseCircleOutlined />}>已禁用</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="配置">
                      {status.configured ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>已配置</Tag>
                      ) : (
                        <Tag color="error" icon={<CloseCircleOutlined />}>未配置</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="初始化">
                      {status.initialized ? (
                        <Tag color="success">已初始化</Tag>
                      ) : (
                        <Tag color="default">未初始化</Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                }
                type={status.configured && status.enabled ? "success" : "warning"}
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            {!status?.configured && (
              <Alert
                message="邮件服务未配置"
                description="请先在邮件配置页面完成 Mailgun 配置"
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <Tabs defaultActiveKey="1">
              <TabPane tab="验证码邮件" key="1">
                <Form
                  form={verificationForm}
                  layout="vertical"
                  onFinish={handleSendVerificationCode}
                >
                  <Form.Item
                    label="收件人邮箱"
                    name="email"
                    rules={[
                      { required: true, message: '请输入收件人邮箱' },
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input placeholder="test@example.com" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!status?.configured}
                    >
                      发送验证码邮件
                    </Button>
                  </Form.Item>
                </Form>
                <Alert
                  message="说明"
                  description="将发送一封包含6位数字验证码的邮件，验证码有效期5分钟"
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              </TabPane>

              <TabPane tab="欢迎邮件" key="2">
                <Form
                  form={welcomeForm}
                  layout="vertical"
                  onFinish={handleSendWelcome}
                >
                  <Form.Item
                    label="收件人邮箱"
                    name="email"
                    rules={[
                      { required: true, message: '请输入收件人邮箱' },
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input placeholder="test@example.com" />
                  </Form.Item>
                  <Form.Item
                    label="用户名"
                    name="username"
                    initialValue="Test User"
                  >
                    <Input placeholder="Test User" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!status?.configured}
                    >
                      发送欢迎邮件
                    </Button>
                  </Form.Item>
                </Form>
                <Alert
                  message="说明"
                  description="将发送一封欢迎新用户的邮件"
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              </TabPane>

              <TabPane tab="密码重置" key="3">
                <Form
                  form={passwordResetForm}
                  layout="vertical"
                  onFinish={handleSendPasswordReset}
                >
                  <Form.Item
                    label="收件人邮箱"
                    name="email"
                    rules={[
                      { required: true, message: '请输入收件人邮箱' },
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input placeholder="test@example.com" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!status?.configured}
                    >
                      发送密码重置邮件
                    </Button>
                  </Form.Item>
                </Form>
                <Alert
                  message="说明"
                  description="将发送一封包含密码重置链接的邮件，链接有效期30分钟"
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              </TabPane>

              <TabPane tab="自定义邮件" key="4">
                <Form
                  form={customForm}
                  layout="vertical"
                  onFinish={handleSendCustom}
                >
                  <Form.Item
                    label="收件人邮箱"
                    name="email"
                    rules={[
                      { required: true, message: '请输入收件人邮箱' },
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input placeholder="test@example.com" />
                  </Form.Item>
                  <Form.Item
                    label="邮件主题"
                    name="subject"
                    initialValue="Test Email"
                  >
                    <Input placeholder="Test Email" />
                  </Form.Item>
                  <Form.Item
                    label="邮件内容"
                    name="content"
                    initialValue="<h1>This is a test email</h1><p>If you received this, the email service is working!</p>"
                  >
                    <TextArea
                      rows={6}
                      placeholder="支持 HTML 格式"
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!status?.configured}
                    >
                      发送自定义邮件
                    </Button>
                  </Form.Item>
                </Form>
                <Alert
                  message="说明"
                  description="可以发送自定义主题和内容的邮件，支持 HTML 格式"
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>

        <Col span={8}>
          <Card
            title="测试记录"
            extra={
              <Button
                size="small"
                onClick={() => setTestResults([])}
              >
                清空
              </Button>
            }
          >
            {testResults.length === 0 ? (
              <Alert
                message="暂无测试记录"
                type="info"
                showIcon
              />
            ) : (
              <List
                size="small"
                dataSource={testResults}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        item.success ? (
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                        )
                      }
                      title={
                        <Space>
                          <Tag color={item.success ? 'success' : 'error'}>
                            {item.type}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.timestamp}
                          </Text>
                        </Space>
                      }
                      description={
                        <div>
                          <div>{item.message}</div>
                          {item.data.email && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              收件人: {item.data.email}
                            </Text>
                          )}
                          {item.data.code && (
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                验证码: <Text code>{item.data.code}</Text>
                              </Text>
                            </div>
                          )}
                          {item.data.messageId && (
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Message ID: {item.data.messageId}
                              </Text>
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EmailTest;
